/**
 * Order Notes (Shopify cart note) for the cart page and cart drawer — see snippets/cart-note.liquid.
 *
 * - Every textarea[data-cart-note] is one view of the same cart note. Typing in one updates the others.
 * - Saves only after the customer edits (never on page load), 600ms after typing stops, and right away on
 *   blur/change, drawer close, and page hide. Saves are serialized, so an older value never lands last.
 * - The drawer footer is re-rendered after every cart change; re-rendered textareas get the latest typed value,
 *   so a refresh rendered from an older server value cannot overwrite what the customer typed.
 * - Checkout: a window-level capture click listener (registered after s-cart-terms.js, so the Terms check still runs
 *   first) holds a checkout click only while a note save is unfinished, waits for it, then repeats the click so the
 *   theme button, Navidium's protected / unprotected buttons, Warnify and STOQ handle it exactly as before.
 *   Native cart-form checkout also posts the note field itself.
 * Endpoint: data-cart-update-url (Liquid routes.cart_update_url) + ".js", per the Ajax Cart API contract.
 */
const NOTE = 'textarea[data-cart-note]';
const navidiumScope = '.nvd-mini, #nvd-cart, [class*="nvd-widget"], [id^="nvd-"], [class^="nvd-"]';

let latest = null; // last value the customer typed in this page view; null = untouched
let saved = null; // last value confirmed by the server in this page view
let timer = 0;
let chain = Promise.resolve();
let releasing = false;

const fields = () => [...document.querySelectorAll(NOTE)];
const endpoint = () => `${fields()[0]?.dataset.cartUpdateUrl || ''}.js`;
const dirty = () => latest !== null && latest !== saved;

function sync(except) {
  if (latest === null) return;
  for (const field of fields()) if (field !== except && field.value !== latest) field.value = latest;
}

function save({ keepalive = false } = {}) {
  clearTimeout(timer);
  timer = 0;
  if (!dirty()) return chain;
  const note = latest;
  chain = chain
    .then(async () => {
      if (note === saved) return;
      const response = await fetch(endpoint(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ note }),
        keepalive,
      });
      if (response.ok) saved = note;
    })
    .catch(() => {});
  return chain;
}

document.addEventListener('input', (event) => {
  const field = event.target;
  if (!(field instanceof HTMLTextAreaElement) || !field.matches(NOTE)) return;
  latest = field.value;
  sync(field);
  clearTimeout(timer);
  timer = setTimeout(save, 600);
});

document.addEventListener('change', (event) => {
  if (event.target instanceof Element && event.target.matches(NOTE)) save();
});
document.addEventListener('focusout', (event) => {
  if (event.target instanceof Element && event.target.matches(NOTE)) save();
});
document.addEventListener('close', () => save(), true); // cart drawer <dialog> closing
window.addEventListener('pagehide', () => save({ keepalive: true }));
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden') save({ keepalive: true });
});

// Re-rendered drawer/cart partials: show the latest typed note, not the possibly older server copy.
new MutationObserver(() => sync(null)).observe(document.documentElement, { childList: true, subtree: true });

function checkoutControl(target) {
  if (!(target instanceof Element)) return null;
  const themeButton = target.closest('[name="checkout"], #dyn-checkout, #nvd-with-checkout-btn, #nvd-without-checkout-btn');
  if (themeButton) return themeButton;
  const control = target.closest('button, a, [role="button"], input[type="submit"]');
  if (control && control.closest(navidiumScope) && /checkout/i.test(control.textContent || control.value || '')) return control;
  return null;
}

window.addEventListener(
  'click',
  (event) => {
    if (releasing) return;
    const control = checkoutControl(event.target);
    if (!control || (!dirty() && !timer)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    save().finally(() => {
      releasing = true;
      try {
        control.click();
      } finally {
        releasing = false;
      }
    });
  },
  true
);
